'use client';

import { Check, RotateCcw } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import {
  COMMISSIONS_QUERY,
  LOCATION_CODES_QUERY,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { BottomSheet } from '@paeffgen/react/crm-documents/ui';
import { clsx } from 'clsx';
import type { FilterState } from './document-filters';

const PERIOD_OPTIONS: { value: FilterState['dateRange']; label: string }[] = [
  { value: 'ALL', label: 'Gesamter Zeitraum' },
  { value: '30DAYS', label: 'Letzte 30 Tage' },
  { value: '3MONTHS', label: 'Letzte 3 Monate' },
  { value: '6MONTHS', label: 'Letzte 6 Monate' },
  { value: '1YEAR', label: 'Letztes Jahr' },
  { value: 'CUSTOM', label: 'Benutzerdefiniert' },
];

interface BelegeFilterSheetProps {
  open: boolean;
  onClose: () => void;
  filters: FilterState;
  onFilterChange: (next: FilterState) => void;
}

export function BelegeFilterSheet({
  open,
  onClose,
  filters,
  onFilterChange,
}: BelegeFilterSheetProps) {
  const { adminQueryVars } = useAdmin();

  const { data: commissionsData } = useQuery<{ commissions: string[] }>(
    COMMISSIONS_QUERY,
    { variables: { ...adminQueryVars }, fetchPolicy: 'cache-first' },
  );

  const { data: locationData } = useQuery<{ locationCodes: string[] }>(
    LOCATION_CODES_QUERY,
    { variables: { ...adminQueryVars }, fetchPolicy: 'cache-first' },
  );

  const commissions = commissionsData?.commissions ?? [];
  const locationCodes = locationData?.locationCodes ?? [];

  const setPeriod = (range: FilterState['dateRange']) => {
    const next = { ...filters, dateRange: range };
    if (range !== 'CUSTOM') {
      next.startDate = undefined;
      next.endDate = undefined;
    }
    onFilterChange(next);
  };

  const reset = () => {
    onFilterChange({
      ...filters,
      dateRange: 'ALL',
      startDate: undefined,
      endDate: undefined,
      minAmount: '',
      maxAmount: '',
      location: '',
      commission: '',
      yourReference: '',
    });
  };

  const update = (patch: Partial<FilterState>) => {
    onFilterChange({ ...filters, ...patch });
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel="Filter"
      header={
        <div className="px-4 py-3 pr-12">
          <h2 className="text-base font-bold text-zinc-900">Filter</h2>
        </div>
      }
      footer={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reset}
            className="shrink-0 px-3 py-3 text-sm font-semibold text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1.5"
          >
            <RotateCcw size={14} /> Zurücksetzen
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-lg py-3 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
          >
            <Check size={16} /> Übernehmen
          </button>
        </div>
      }
    >
      <div className="px-4 pt-3 pb-4 space-y-5">
        <section>
          <p className="text-[10px] font-extrabold text-zinc-500 uppercase tracking-widest mb-2">
            Zeitraum
          </p>
          <div className="space-y-1">
            {PERIOD_OPTIONS.map((opt) => {
              const isActive = filters.dateRange === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPeriod(opt.value)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                    isActive
                      ? 'bg-orange-50'
                      : 'hover:bg-zinc-50 active:bg-zinc-100',
                  )}
                >
                  <span
                    className={clsx(
                      'flex-1 text-sm font-medium',
                      isActive ? 'text-primary font-bold' : 'text-zinc-900',
                    )}
                  >
                    {opt.label}
                  </span>
                  {isActive && (
                    <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {filters.dateRange === 'CUSTOM' && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => update({ startDate: e.target.value })}
                className="flex-1 px-3 py-2 border border-zinc-200 rounded-lg outline-none focus:border-primary text-sm"
              />
              <span className="text-zinc-400">–</span>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => update({ endDate: e.target.value })}
                className="flex-1 px-3 py-2 border border-zinc-200 rounded-lg outline-none focus:border-primary text-sm"
              />
            </div>
          )}
        </section>

        <section>
          <p className="text-[10px] font-extrabold text-zinc-500 uppercase tracking-widest mb-2">
            Erweiterte Filter
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">
                Betrag (Netto)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Von"
                  value={filters.minAmount || ''}
                  onChange={(e) => update({ minAmount: e.target.value })}
                  className="flex-1 px-3 py-2 border border-zinc-200 rounded-lg outline-none focus:border-primary text-sm"
                />
                <span className="text-zinc-400">–</span>
                <input
                  type="number"
                  placeholder="Bis"
                  value={filters.maxAmount || ''}
                  onChange={(e) => update({ maxAmount: e.target.value })}
                  className="flex-1 px-3 py-2 border border-zinc-200 rounded-lg outline-none focus:border-primary text-sm"
                />
              </div>
            </div>

            {locationCodes.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Standort
                </label>
                <select
                  value={filters.location || ''}
                  onChange={(e) => update({ location: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-200 rounded-lg outline-none focus:border-primary text-sm bg-white"
                >
                  <option value="">Alle Standorte</option>
                  {locationCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {commissions.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Kommission / Baustelle
                </label>
                <select
                  value={filters.commission || ''}
                  onChange={(e) => update({ commission: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-200 rounded-lg outline-none focus:border-primary text-sm bg-white"
                >
                  <option value="">Alle Kommissionen</option>
                  {commissions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">
                Ihre Referenz
              </label>
              <input
                type="text"
                placeholder="Referenz suchen…"
                value={filters.yourReference || ''}
                onChange={(e) => update({ yourReference: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-200 rounded-lg outline-none focus:border-primary text-sm"
              />
            </div>
          </div>
        </section>
      </div>
    </BottomSheet>
  );
}
